import { PermissionRole } from '@aline/common';
import { Apollo } from 'src/gql/generated';
import { roleAvatarColor } from 'src/theme';
import type {
  AccountSubset,
  GroupSubset,
  OrgSubset,
  UserDefinedRoleSubset,
  UserSubset,
} from 'src/types';

export function getIsUserSubset(accountSubset: AccountSubset): accountSubset is UserSubset {
  return (accountSubset as UserSubset).email !== undefined;
}

export function getEntityDisplayDetails(
  entity?:
    | Omit<OrgSubset, 'id'>
    | Omit<UserSubset, 'id'>
    | Omit<GroupSubset, 'id'>
    | Omit<UserDefinedRoleSubset, 'id'>
    | null
): {
  primary: string;
  avatarColor?: string;
  secondary?: string;
} {
  if (!entity) return { primary: 'Unassigned' };

  switch (entity.__typename) {
    case 'User': {
      return {
        avatarColor: entity.avatarColor,
        primary: entity.displayName,
        secondary: entity.email,
      };
    }
    case 'Group':
      return { primary: entity.name };
    case 'Org':
      return {
        avatarColor: entity.avatarColor,
        primary: entity.name,
        secondary: '(Entire Organization)',
      };
    case 'UserDefinedRole':
      return { avatarColor: roleAvatarColor, primary: entity.roleName };
  }
}

export function getUserName(user: UserSubset): string {
  return `${user.firstName || ''} ${user.lastName || ''}`.trim();
}

export interface StorableWithRoleInfo extends Pick<
  Apollo.Storable,
  '__typename' | 'requesterRole'
> {
  owner: Pick<Apollo.Storable['owner'], '__typename' | 'id'>;
}

export interface MostPermissiveUserStorableRole {
  isEditor: boolean;
  isOwner: boolean;
  role: PermissionRole | null;
  roleString: PermissionRoleString;
}

enum PermissionRoleString {
  Comment = 'Comment',
  Edit = 'Edit',
  None = '',
  Suggest = 'Suggest',
  View = 'View',
}

export function getMostPermissiveUserStorableRole(
  userId?: UUID | null,
  storable?: StorableWithRoleInfo | null
): MostPermissiveUserStorableRole {
  if (!storable)
    return { isEditor: false, isOwner: false, role: null, roleString: PermissionRoleString.None };

  // User permissions doesn't include if the user is the storable owner, so check that first.
  const isOwner = storable.owner.id === userId;
  const isEditor = storable.requesterRole === PermissionRole.Editor;
  if (isOwner)
    return {
      isEditor,
      isOwner,
      role: PermissionRole.Owner,
      roleString: getPermissionRoleText(PermissionRole.Owner),
    };

  return {
    isEditor,
    isOwner,
    role: storable.requesterRole,
    roleString: getPermissionRoleText(storable.requesterRole),
  };
}

export function getPermissionRoleText(role: PermissionRole | null): PermissionRoleString {
  if (!role) return PermissionRoleString.None;

  switch (role) {
    case PermissionRole.Owner:
    case PermissionRole.Editor:
      return PermissionRoleString.Edit;
    case PermissionRole.Suggester:
      return PermissionRoleString.Suggest;
    case PermissionRole.Commenter:
      return PermissionRoleString.Comment;
    case PermissionRole.Viewer:
      return PermissionRoleString.View;
  }
}

export function getIsBasicUser(
  user: Pick<Apollo.User, 'isDeactivated' | 'role' | '__typename'>
): boolean {
  return !user.isDeactivated && user.role === Apollo.OrgRole.Basic;
}

export function getIsFullUser(
  user: Pick<Apollo.User, 'isDeactivated' | 'role' | '__typename'>
): boolean {
  return !user.isDeactivated && [Apollo.OrgRole.Admin, Apollo.OrgRole.Full].includes(user.role);
}
